<template>
	<v-app>
		<v-navigation-drawer
			v-model="drawerStart"
			app
			clipped
			:width="410"
		>
			<template v-if="atlasPagesListLoading || pageDataLoading">
				<v-progress-circular
					indeterminate
					color="primary"
					:size="40"
					width="2"
					class="ma-3"
				></v-progress-circular>
			</template>
			<template v-else>
				<template v-if="pageContentManagerComponent">
					<component
						:is="pageContentManagerComponent"
						v-bind:cfg="pageContentProperties"
					></component>
				</template>
			</template>
		</v-navigation-drawer>

		<v-navigation-drawer
			v-model="drawerEnd"
			app
			clipped
			right
			:class="[pagesListOrderChangeInAction? 'visually-disabled' : '']"
			:width="type == 'edit' ? 210 : 120"
		>
			<template v-if="atlasPagesListLoading">
				<v-progress-circular
					indeterminate
					color="primary"
					:size="30"
					width="2"
					class="ma-3"
				></v-progress-circular>
			</template>
			<template v-else>
				<template v-if="pagesList">
					<v-item-group
						v-model="currentPageNumber"
						mandatory
						active-class="active-page"
					>
						<draggable
							v-model="pagesList"
							group="pagesList"
							@start="drag = true"
							@end="drag = false"
							v-bind="dragOptions"
							v-on:update="onPagesListOrderChange"
						>
							<transition-group
								type="transition"
								name="!drag ? 'flip-list' : null"
							>
								<template v-for="(page, index) in pagesList">
									<v-item
										v-slot:default="{ active, toggle }"
										:key="index"
									>
										<AtlasPagePreview
											:data="page"
											:atlasId="atlasId"
											class="ml-3 mt-3 mr-3"
											:refreshAtlasPagesList="getAtlasPagesList"
											:clickHandler="toggle"
											:canBeDeleted="(type == 'edit') ? (pagesList.length > 1) : false"
											:index="index"
										/>
									</v-item>
								</template>
							</transition-group>
						</draggable>
					</v-item-group>
					<v-btn
						v-on:click="showPageTemplateSelector()"
						class="ma-3"
						:loading="newPageCreationInAction"
						small
						v-if="type == 'edit' && atlasData['atlasType'] != 'printable'"
					>
						Pridėti naują puslapį
					</v-btn>
				</template>
			</template>
		</v-navigation-drawer>

		<v-app-bar
			app
			clipped-left
			clipped-right
		>
			<v-app-bar-nav-icon @click.stop="toggleDrawers"></v-app-bar-nav-icon>
			<Title
				v-bind:title="atlasMetaLoading ? '{loading}' : (atlasData ? atlasData['title'] : 'Klaida...')"
				v-bind:isAtlas="{type: type, id: atlasId}"
			/>
		</v-app-bar>

		<v-content>
			<template v-if="atlasPagesListLoading || pageDataLoading">
				<v-progress-circular
					indeterminate
					color="primary"
					:size="40"
					width="2"
					class="ma-3"
				></v-progress-circular>
			</template>
			<template v-else>
				<template v-if="pageContentComponent">
					<v-container fill-height flex-column align-stretch flex-nowrap fluid class="pa-0 ma-0">
						<v-container
							align-center
							fluid
							class="pa-2 my-toolbar d-flex"
							v-if="type == 'edit'"
						>
							<div
								class="flex-grow-1 d-flex align-center"
								v-if="atlasData['atlasType'] != 'printable'"
							>
								<v-btn
									v-on:click="preview"
									small
									class="mr-4"
								>
									Peržiūrėti
								</v-btn>
								<v-checkbox
									label="Sinchronizuoti žemėlapius"
									v-model="mapsSync"
									v-if="pageContentComponent == 'multiple-cards-template' && mapsSyncAvailable"
									class="mt-0 d-inline-block"
									dense
									hide-details
								></v-checkbox>
							</div>
							<v-btn
								v-on:click="saveAtlasPage"
								:loading="saveInAction"
								small
							>
								Išsaugoti
							</v-btn>
						</v-container>
						<div class="flex-grow-1 my-component-wrapper">
							<component
								:is="pageContentComponent"
								v-bind:cfg="pageContentProperties"
								ref="page"
							></component>
						</div>
					</v-container>
				</template>
				<template v-else-if="atlasData">
					<div class="ma-3">
						<div>Nenurodytas puslapio šablonas!</div>
						<v-btn
							v-on:click="showPageTemplateSelector(currentPageId)"
							class="mt-3"
							v-if="type == 'edit' && atlasData['atlasType'] != 'printable'"
							small
						>
							Pasirinkti puslapio šabloną
						</v-btn>
					</div>
				</template>
				<template v-else-if="atlasMetaLoadingError">
					<div class="ma-4">Deja, atlaso informacija nepasiekiama...</div>
				</template>
			</template>
			<PageTemplateSelector />
			<CommonComponents />
			<LegendDialog />
		</v-content>

		<v-footer app>
			<Footer />
		</v-footer>
	</v-app>
</template>

<script>
	import Title from "../header/Title";
	import Footer from "../Footer";
	import Loading from "../Loading";
	import CommonHelper from "../helpers/CommonHelper";
	import AtlasHelper from "../helpers/AtlasHelper";
	import AtlasPagePreview from "../atlas/AtlasPagePreview";
	import PageTemplateSelector from "../atlas/PageTemplateSelector"; // FIXME! Aktualu tik "edit" režime? Režime "view" tai nėra aktualu... Režime "view" to net nekrauti!!!
	import CommonComponents from "../CommonComponents";
	import draggable from "vuedraggable";
	import LegendDialog from "../atlas/LegendDialog";

	export default {
		props: {
			cfg: Object
		},
		components: {
			Title,
			Footer,
			AtlasPagePreview,
			PageTemplateSelector,
			LegendDialog,
			"multiple-cards-template": () => ({
				component: import("../atlas/page-templates/MultipleCards"),
				loading: Loading
			}),
			"objects-template": () => ({
				component: import("../atlas/page-templates/Objects"),
				loading: Loading
			}),
			"print-template": () => ({
				component: import("../atlas/page-templates/Print"),
				loading: Loading
			}),
			"multiple-cards-template-edit": () => ({
				component: import("../atlas/page-templates/manager/MultipleCardsEdit"),
				loading: Loading
			}),
			"multiple-cards-template-view": () => ({
				component: import("../atlas/page-templates/manager/MultipleCardsView"),
				loading: Loading
			}),
			"objects-template-edit": () => ({
				component: import("../atlas/page-templates/manager/ObjectsEdit"),
				loading: Loading
			}),
			"objects-template-view": () => ({
				component: import("../atlas/page-templates/manager/ObjectsView"),
				loading: Loading
			}),
			"print-template-edit": () => ({
				component: import("../atlas/page-templates/manager/PrintEdit"),
				loading: Loading
			}),
			"print-template-view": () => ({
				component: null
			}),
			CommonComponents,
			draggable
		},
		data: function(){
			var data = {
				drawerStart: false,
				drawerEnd: false,
				atlasMetaLoading: true,
				atlasMetaLoadingError: true,
				atlasPagesListLoading: true,
				pageDataLoading: false,
				atlasData: null,
				pagesList: null,
				newPageCreationInAction: false,
				currentPageNumber: null,
				currentPageId: null,
				type: null,
				pageData: null,
				pageContentComponent: null,
				pageContentProperties: null,
				saveInAction: false,
				pageContentManagerComponent: null,
				mapsSync: false,
				mapsSyncAvailable: false,
				dragOptions: {
					animation: 200,
					group: "features",
					disabled: false,
					ghostClass: "ghost"
				},
				drag: false,
				pagesListOrderChangeInAction: false
			};
			return data;
		},
		created: function(){
			var path = this.$router.history.current.path,
				atlasPathData = CommonHelper.getAtlasPathData(path);
			if (atlasPathData) {
				this.atlasId = atlasPathData[0];
				this.type = atlasPathData[1];
				this.$store.commit("setCurrentAtlasDisplayType", this.type);
				AtlasHelper.getAtlasData(this.atlasId).then(function(data){
					this.atlasData = data;
					this.atlasMetaLoading = false;
					if (this.atlasData["atlasType"] == "interactive") {
						this.drawerEnd = true;
					}
					this.getAtlasPagesList();
				}.bind(this), function(){
					this.atlasMetaLoadingError = true;
					this.atlasMetaLoading = false;
					this.atlasPagesListLoading = false;
				}.bind(this));
			} else {
				// Taip net teoriškai nelabai galėtų būti...
			}
		},
		methods: {
			toggleBodyClass: function(addRemoveClass, className){
				// https://forum.vuejs.org/t/how-do-i-add-remove-classes-to-body/1219/12
				var el = document.documentElement;
				if (addRemoveClass === "addClass") {
					el.classList.add(className);
				} else {
					el.classList.remove(className);
				}
			},
			toggleDrawers: function(){
				if (this.drawerStart) {
					this.drawerStart = false;
				} else {
					this.drawerStart = true;
					if (this.atlasData && this.atlasData["atlasType"] == "interactive") {
						this.drawerEnd = true;
					}
				}
			},
			getAtlasPagesList: function(pageId){
				this.atlasPagesListLoading = true;
				AtlasHelper.getAtlasPagesList(this.atlasId).then(function(pagesList){
					if (this.atlasData["pageOrder"]) {
						var pagesListOrder = this.atlasData["pageOrder"].split(",");
						if (pagesListOrder) {
							var orderedPagesList = [];
							pagesListOrder.forEach(function(pageNumber){
								pagesList.some(function(page){
									if (page["pageID"] == pageNumber) {
										page.used = true;
										orderedPagesList.push(page);
										return true;
									}
								});
							});
							pagesList.forEach(function(page){
								if (!page.used) {
									orderedPagesList.push(page);
									console.warn("PAGE NOT IN pagesListOrder...", page); // Pvz. taip bus jei pridedame naują puslapį ir neišsaugome puslapio ID puslapių order'inimo sąraše...
								} else {
									delete page.used;
								}
							});
							pagesList = orderedPagesList;
						}
					}
					this.pagesList = pagesList;
					this.atlasPagesListLoading = false;
					var currentPageNumber;
					if (!pageId) {
						pageId = this.currentPageId;
					}
					if (pageId) {
						this.pagesList.some(function(page, i){
							if (pageId == page["pageID"]) {
								currentPageNumber = i;
								return true;
							}
						}.bind(this));
						if (!currentPageNumber) {
							if (this.currentPageNumber) {
								if (this.pagesList.length > this.currentPageNumber) {
									currentPageNumber = this.currentPageNumber;
								} else {
									currentPageNumber = this.currentPageNumber - 1;
								}
							}
						}
					}
					this.currentPageNumber = currentPageNumber;
					this.setCurrentPageId();
					// FIXME? Dabar belieka vienas netikslumas... Tarkime, kad turime penkis psl. ir aktyvus yra trečias... Ištriname trečią... Tad dabar aktyvus tampa pirmas... Gal reiktų tolimesnį eilėje rodyti?..
				}.bind(this), function(){
					this.atlasPagesListLoading = false;
				}.bind(this));
			},
			createNewPage: function(callback, pageTemplateId){
				this.newPageCreationInAction = true;
				var pageContent = this.getPageContent4Save(pageTemplateId);
				AtlasHelper.createNewPage(this.atlasId, pageContent).then(function(pageId){
					this.newPageCreationInAction = false;
					this.getAtlasPagesList(pageId);
					callback(true);
				}.bind(this), function(){
					this.$vBus.$emit("show-message", "error");
					this.newPageCreationInAction = false;
					callback(false);
				}.bind(this));
			},
			updatePage: function(pageId, callback, pageTemplateId){
				var pageContent = this.getPageContent4Save(pageTemplateId);
				AtlasHelper.updatePage(this.atlasId, pageId, pageContent).then(function(){
					this.getPageData(this.currentPageId);
					callback(true);
				}.bind(this), function(){
					this.$vBus.$emit("show-message", "error");
					callback(false);
				}.bind(this));
			},
			getPageData: function(pageId){
				this.pageDataLoading = true;
				this.pageContentComponent = null;
				this.pageContentProperties = null;
				this.pageContentManagerComponent = null;
				this.$store.commit("setActiveWidget", null);
				this.$store.commit("setCurrentPageMaps", null);
				this.mapsSync = false;
				AtlasHelper.getPage(this.atlasId, pageId).then(function(pageData){
					this.pageData = pageData;
					if (this.atlasData["atlasType"] == "printable") {
						// Čia toks įdomesnis atvejis... Spausdinimui skirtas atlasas gali turėti tik vieno tipo puslapį...
						if (!pageData["templateContent"]) {
							pageData["templateContent"] = {
								template: "print"
							};
						}
					}
					if (pageData["templateContent"]) {
						this.setPageContentComponentAndProperties(pageData);
						if (pageData["templateContent"].data) {
							this.initialMapsSync = pageData["templateContent"].data.mapsSync;
						}
					} else {
						if (this.type == "edit") {
							this.showPageTemplateSelector(pageId);
							this.drawerStart = false;
						}
					}
					this.setPageContentManagerComponent();
					this.pageDataLoading = false;
				}.bind(this), function(){
					this.$vBus.$emit("show-message", "error");
					this.pageDataLoading = false;
				}.bind(this));
			},
			setCurrentPageId: function(){
				if (this.currentPageNumber != null && this.pagesList[this.currentPageNumber]) { // Antra sąlyga aktuali kai greit šaliname kelis puslapius??...
					this.currentPageId = this.pagesList[this.currentPageNumber]["pageID"];
				}
			},
			showPageTemplateSelector: function(pageId){
				this.$vBus.$emit("show-page-template-selector", {
					callback: function(pageTemplateId, callback){
						if (!pageId) {
							this.createNewPage(callback, pageTemplateId);
						} else {
							this.updatePage(pageId, callback, pageTemplateId);
						}
					}.bind(this)
				});
			},
			setPageContentComponentAndProperties: function(pageData){
				var pageContentComponent,
					pageContentProperties = {};
				if (pageData["templateContent"] && pageData["templateContent"].template) {
					switch (pageData["templateContent"].template) {
						case "objects":
							pageContentComponent = "objects-template";
							pageContentProperties.data = pageData["templateContent"].data;
							break;
						case "print":
							pageContentComponent = "print-template";
							pageContentProperties.data = pageData["templateContent"].data;
							if (!pageContentProperties.data) {
								pageContentProperties.data = {};
							}
							pageContentProperties.data.atlasData = this.atlasData; // TODO: gal klonuoti?...
							break;
						default:
							pageContentComponent = "multiple-cards-template";
							var gridData = pageData["templateContent"].template.split("x");
							if (gridData.length == 2) {
								gridData = {
									rows: parseInt(gridData[0]),
									cols: parseInt(gridData[1])
								};
							}
							pageContentProperties.grid = gridData;
							if (pageData["templateContent"].data) {
								pageContentProperties.cells = pageData["templateContent"].data.cells;
							}
					}
				}
				this.pageContentComponent = pageContentComponent;
				this.pageContentProperties = pageContentProperties;
			},
			getPageContent4Save: function(pageTemplateId, data){
				if (data && this.mapsSync) {
					data.mapsSync = this.mapsSync;
				}
				var content = {
					template: pageTemplateId,
					data: data
				};
				content = JSON.stringify(content);
				return content;
			},
			saveAtlasPage: function(){
				if (this.$refs.page && this.$refs.page.getPageData4Save) {
					this.saveInAction = true; // TODO: reikia neleisti pereiti į kitą puslapį? Išvis reikia neleisti niekur kitur pereiti?..
					var pageTemplateId;
					if (this.pageData["templateContent"]) {
						pageTemplateId = this.pageData["templateContent"].template;
					}
					if (!pageTemplateId) {
						if (this.atlasData["atlasType"] == "printable") {
							pageTemplateId = "print";
						}
					}
					var pageData4Save = this.$refs.page.getPageData4Save(),
						pageContent = this.getPageContent4Save(pageTemplateId, pageData4Save);
					AtlasHelper.updatePage(this.atlasId, this.pageData.pageId, pageContent).then(function(){
						this.saveInAction = false;
						// TODO: kažkaip simboliškai pranešti, kad viskas OK? Sekundei pasirodo animuota žalia varnelė prie mygtuko "Išsaugoti"?..
					}.bind(this), function(){
						this.$vBus.$emit("show-message", "error");
						this.saveInAction = false;
					}.bind(this));
				} else {
					this.$vBus.$emit("show-message", "error");
				}
			},
			setPageContentManagerComponent: function(){
				if (this.pageContentComponent) {
					this.pageContentManagerComponent = this.pageContentComponent + "-" + this.type;
					if (this.type != "view") {
						this.drawerStart = true;
					}
				}
			},
			addMapToMapsCollection: function(map){
				this.maps.push(map);
				this.$store.commit("setCurrentPageMaps", this.maps);
			},
			removeMapFromMapsCollection: function(map){
				var index = this.maps.indexOf(map);
				if (index !== -1) {
					this.maps.splice(index, 1);
					this.$store.commit("setCurrentPageMaps", this.maps);
				}
			},
			onPagesListOrderChange: function(){
				var currentPageNumber;
				this.pagesList.some(function(page, i){
					if (this.currentPageId == page["pageID"]) {
						currentPageNumber = i;
						return true;
					}
				}.bind(this));
				this.currentPageNumber = currentPageNumber;
				this.savePagesListOrder();
			},
			savePagesListOrder: function(){
				this.pagesListOrderChangeInAction = true;
				var list = [];
				this.pagesList.some(function(page){
					list.push(page["pageID"]);
				}.bind(this));
				var pageOrder = list.join();
				AtlasHelper.updatePageOrder(this.atlasId, pageOrder).then(function(){
					this.pagesListOrderChangeInAction = false;
				}.bind(this), function(){
					this.$vBus.$emit("show-message", "error");
					this.pagesListOrderChangeInAction = false;
					// FIXME! kas dabar??? Kaip vartotojui leisti bandyti išsaugoti vėl? Pakeisti puslapių eiliškumą į bet kokį ir vėl atkeisti į norimą?..
				}.bind(this));
			},
			preview: function(){
				window.open(location.href.replace("/edit", "/view"), "_blank"); // Arba nuorodą galima išgauti iš this.$router.resolve?!
			}
		},
		mounted(){
			this.toggleBodyClass("addClass", "atlas");
			this.maps = [];
			this.$vBus.$on("map-added", this.addMapToMapsCollection);
			this.$vBus.$on("map-removed", this.removeMapFromMapsCollection);
		},
		beforeDestroy(){
			this.$vBus.$off("map-added", this.addMapToMapsCollection);
			this.$vBus.$off("map-removed", this.removeMapFromMapsCollection);
		},
		destroyed: function(){
			this.toggleBodyClass('removeClass', "atlas");
		},
		watch: {
			currentPageNumber: {
				immediate: true,
				handler: function(){
					this.setCurrentPageId();
				}
			},
			currentPageId: {
				immediate: true,
				handler: function(pageId){
					if (pageId != null) {
						this.getPageData(pageId);
					}
				}
			},
			"$store.state.currentPageMaps": {
				immediate: true,
				handler: function(maps){
					if (maps && maps.length > 1) {
						this.mapsSyncAvailable = true;
						if (this.initialMapsSync) {
							this.mapsSync = true;
							this.initialMapsSync = null;
						}
					} else {
						this.mapsSyncAvailable = false;
						this.mapsSync = false;
						this.$store.commit("setRefMapView");
					}
				}
			},
			"$store.state.activeWidget": {
				immediate: true,
				handler: function(activeWidget){
					if (activeWidget) {
						if (this.type == "view") {
							if (activeWidget.content && activeWidget.content.type == "map" && activeWidget.content.data && activeWidget.content.data.myMap && !activeWidget.content.data.myMap.noLegendInPreview) {
								this.drawerStart = true;
							}
						} else {
							this.drawerStart = true;
						}
					} else {
						if (this.type == "view") {
							this.drawerStart = false;
						}
					}
				}
			},
			mapsSync: {
				immediate: true,
				handler: function(val){
					if (val) {
						this.$store.commit("setRefMapView", this.maps[0].getView()); // FIXME! Masyve pirmas žemėlapis nebūtinai pirmas vizualiai... Tai bus pirmas pridėtas žemėlapis! Jei tai nepatinka, reikia sekti map'o widget'o numerį ir imti mažiausią?..
					} else {
						this.$store.commit("setRefMapView");
					}
				}
			}
		}
	};
</script>

<style scoped>
	.v-content {
		height: 100vh;
	}
	.my-toolbar {
		border: thin solid rgba(0, 0, 0, 0.12);
	}
	.my-component-wrapper {
		overflow: auto;
	}
	.visually-disabled {
		opacity: 0.4;
		pointer-events: none;
	}
</style>